/* eslint-disable indent */
import numberUtils, {
  parseCountThousand,
  parseCountAbbrv2,
} from 'utils/number';
import numeral from 'numeral';
import number from 'utils/number';
import { handleCSingle, handleOsingle } from 'utils/websocketHandler';
import wsUtils from 'utils/webSocket';
import { setValue } from 'global/MainLayout/SideWidget/Watchlist/Orderbook/store';
import {
  CompanyOrderbook,
  OrderbookPriceData,
  OrderbookPriceSummary,
} from '../../../../../@types/orderbook';
import { OrderbookText } from './styled';

const getType = (data) => {
  const formattedData = numeral(data).value();
  if (formattedData > 0) {
    return 'up';
  }

  if (formattedData < 0) {
    return 'down';
  }

  return 'notchange';
};

const getTypeByComparison = (data, dataToCompare, yellowZero = false) => {
  const formattedData = numeral(data).value();
  const formattedDataToCompare = numeral(dataToCompare).value();

  if (yellowZero && !formattedData) return 'notchange';

  if (formattedData > formattedDataToCompare) {
    return 'up';
  }

  if (formattedData < formattedDataToCompare) {
    return 'down';
  }

  if (formattedData === formattedDataToCompare) {
    return 'notchange';
  }

  return '';
};

const formatPercentageChange = (value) => {
  const percentData = value || 'NA';
  return percentData || percentData !== 'NA'
    ? `${numeral(percentData).format('0.00')}%`
    : 'NA';
};

const formatAverage = (value) => Number.isNaN(value) || Number(value) === 0 || !value
    ? 'NA'
    : parseCountThousand(value);

const formatNumber = (value) => numeral(value).format('0,0[.]00');

const formatNumberWithNA = (value, NA = 'NA') =>
  value ? parseCountThousand(value) : NA;

const formatLastPrice = (value) => (value ? parseCountThousand(value) : 'NA');

const formatChangePrevious = (value) => {
  if (numberUtils.isDecimal(value)) {
    return numeral(value).format('0.00');
  }
  return value !== 'NA' ? numeral(value).format('0') : 'NA';
};

const generateOHLCData = (data: Partial<CompanyOrderbook>) => {
  const fourthRowExist = !!data.iepiev || data.is_foreignbs_exist;
  return [
    // First row
    {
      col1Label: <OrderbookText padding="0px 5px 4px 12px">Prev</OrderbookText>,
      col1Value: (
        <OrderbookText padding="0px 5px 4px" type="notchange">
          {formatNumber(data.previous)}
        </OrderbookText>
      ),
      col2Label: <OrderbookText padding="0px 5px 4px">Open</OrderbookText>,
      col2Value: (
        <OrderbookText
          padding="0px 5px 4px"
          type={getTypeByComparison(data.open, data.previous)}
        >
          {formatNumber(data.open)}
        </OrderbookText>
      ),
      col3Label: <OrderbookText padding="0px 5px 4px">Lot</OrderbookText>,
      col3Value: (
        <OrderbookText padding="0px 12px 4px 5px" type={getType(data.change)}>
          {data.volume
            ? number.parseCountAbbrv3(number.parseLot(data.volume), 1, 4)
            : 'NA'}
        </OrderbookText>
      ),
    },

    // Second row
    {
      col1Label: <OrderbookText padding="4px 5px 4px 12px">Chg</OrderbookText>,
      col1Value: (
        <OrderbookText padding="4px 5px 4px" type={getType(data.change)}>
          {number.addPlusOrMinus2(data.change)}
        </OrderbookText>
      ),
      col2Label: <OrderbookText padding="4px 5px 4px">High</OrderbookText>,
      col2Value: (
        <OrderbookText
          padding="4px 5px 4px"
          type={getTypeByComparison(data.high, data.previous)}
        >
          {formatNumber(data.high)}
        </OrderbookText>
      ),
      col3Label: <OrderbookText padding="4px 5px 4px">Val</OrderbookText>,
      col3Value: (
        <OrderbookText padding="4px 12px 4px 5px" type={getType(data.change)}>
          {data.value ? parseCountAbbrv2(data.value, 1) : 'NA'}
        </OrderbookText>
      ),
    },

    // Third row
    {
      col1Label: (
        <OrderbookText
          padding={`4px 5px ${fourthRowExist ? '4px' : '0px'} 12px`}
        >
          %
        </OrderbookText>
      ),
      col1Value: (
        <OrderbookText
          padding={`4px 5px ${fourthRowExist ? '4px' : '0px'}`}
          type={getType(data.percentage_change)}
        >
          {formatPercentageChange(data.percentage_change)}
        </OrderbookText>
      ),
      col2Label: (
        <OrderbookText padding={`4px 5px ${fourthRowExist ? '4px' : '0px'}`}>
          Low
        </OrderbookText>
      ),
      col2Value: (
        <OrderbookText
          padding={`4px 5px ${fourthRowExist ? '4px' : '0px'}`}
          type={getTypeByComparison(data.low, data.previous)}
        >
          {formatNumber(data.low)}
        </OrderbookText>
      ),
      col3Label: (
        <OrderbookText padding={`4px 5px ${fourthRowExist ? '4px' : '0px'}`}>
          Avg
        </OrderbookText>
      ),
      col3Value: (
        <OrderbookText
          padding={`4px 12px ${fourthRowExist ? '4px' : '0px'} 5px`}
          type={getType(data.change)}
        >
          {formatAverage(data.average)}
        </OrderbookText>
      ),
    },

    // Fourth row
    data.iepiev
      ? {
          col1Label: (
            <OrderbookText padding="4px 5px 0px 12px">Freq</OrderbookText>
          ),
          col1Value: (
            <OrderbookText padding="4px 5px 0px">
              {data.frequency ? numeral(data.frequency).format('0,0') : ''}
            </OrderbookText>
          ),
          col2Label: <OrderbookText padding="4px 5px 0px">IEP</OrderbookText>,
          col2Value: (
            <OrderbookText
              padding="4px 5px 0px"
              type={
                data.iepiev.iep.raw
                  ? getTypeByComparison(data.iepiev.iep.raw, data.previous)
                  : undefined
              }
            >
              {data.iepiev.iep.raw ? formatNumber(data.iepiev.iep.raw) : '-'}
            </OrderbookText>
          ),
          col3Label: <OrderbookText padding="4px 5px 0px">IEV</OrderbookText>,
          col3Value: (
            <OrderbookText padding="4px 12px 0px 5px">
              {data.iepiev.iev.raw
                ? parseCountAbbrv2(data.iepiev.iev.raw, 1, ',', true)
                : '-'}
            </OrderbookText>
          ),
        }
      : data.is_foreignbs_exist
      ? {
          col1Label: (
            <OrderbookText padding="4px 5px 0px 12px">Freq</OrderbookText>
          ),
          col1Value: (
            <OrderbookText padding="4px 5px 0px">
              {data.frequency ? numeral(data.frequency).format('0,0') : ''}
            </OrderbookText>
          ),
          col2Label: <OrderbookText padding="4px 5px 0px">F Buy</OrderbookText>,
          col2Value: (
            <OrderbookText padding="4px 5px 0px">
              {data.fbuy ? numeral(data.fbuy).format('0.0 a') : ''}
            </OrderbookText>
          ),
          col3Label: (
            <OrderbookText padding="4px 5px 0px">F Sell</OrderbookText>
          ),
          col3Value: (
            <OrderbookText padding="4px 12px 0px 5px">
              {data.fsell ? numeral(data.fsell).format('0.0 a') : ''}
            </OrderbookText>
          ),
        }
      : {},
  ];
};

const generateOrderbookData = (
  data: CompanyOrderbook | Partial<Pick<CompanyOrderbook, 'bid' | 'offer'>>,
  length = 10,
): Array<OrderbookPriceData> => {
  const { bid = {}, offer = {} } = data;

  return [...Array(length)].map((_, i) => ({
    bidLot: parseInt(bid?.[`volume${i + 1}`], 10) / 100 || 0,
    bidPrice: parseInt(bid?.[`price${i + 1}`], 10) || 0,
    bidFrequency: parseInt(bid?.[`que_num${i + 1}`], 10) || 0,
    offerPrice: parseInt(offer?.[`price${i + 1}`], 10) || 0,
    offerLot: parseInt(offer?.[`volume${i + 1}`], 10) / 100 || 0,
    offerFrequency: parseInt(offer?.[`que_num${i + 1}`], 10) || 0,
  }));
};

const handleReceiveMessageNATSLivePrice = (message) => {
  handleCSingle(message, true);
};

const handleReceiveMessageNATSOrderbook = (message) => {
  const parsedData = wsUtils.parseSocketString(message.body);
  if (parsedData.O) {
    handleOsingle(parsedData.O);
  }
};

const handleReceiveMessageNATSIepIev = (
  symbol: string,
  value: null | {
    iep: number;
    iev: number;
  },
) => {
  setValue({
    key: 'liveprice',
    type: 'update_iepiev',
    symbol,
    value,
  });
};

interface BidOfferPrice {
  bidPrice: Array<string>;
  offerPrice: Array<string>;
}

const getBidOfferPrice = (
  orderbookData: Partial<Pick<CompanyOrderbook, 'bid' | 'offer'>> = {},
): BidOfferPrice => {
  const { bid, offer } = orderbookData;
  const newBid = bid || {};
  const newOffer = offer || {};

  const bidPrice = Object.keys(newBid).filter((key) => key.includes('price'));
  const offerPrice = Object.keys(newOffer).filter((key) =>
    key.includes('price'),
  );

  return { bidPrice, offerPrice };
};

const getPriceDataFromOrderbookData = (
  orderbookData: Partial<Pick<CompanyOrderbook, 'bid' | 'offer'>> = {},
): Array<OrderbookPriceData> => {
  const { bidPrice, offerPrice } = getBidOfferPrice(orderbookData);

  const dataLength = Math.max(bidPrice.length, offerPrice.length);

  const dataShowedLength = dataLength <= 10 ? 10 : dataLength;
  return generateOrderbookData(orderbookData, dataShowedLength);
};

const calculateSummary = (
  priceData: Array<OrderbookPriceData> = [],
): OrderbookPriceSummary => {
  const totalLots = priceData.reduce(
    (obj, curr) => ({
      bid: obj.bid + Number(curr.bidLot),
      offer: obj.offer + Number(curr.offerLot),
      bidFrequency: obj.bidFrequency + Number(curr.bidFrequency),
      offerFrequency: obj.offerFrequency + Number(curr.offerFrequency),
    }),
    { bid: 0, offer: 0, bidFrequency: 0, offerFrequency: 0 },
  );

  return {
    bid: numeral(totalLots.bid).format('0,0'),
    offer: numeral(totalLots.offer).format('0,0'),
    bidFrequency: numeral(totalLots.bidFrequency).format('0,0'),
    offerFrequency: numeral(totalLots.offerFrequency).format('0,0'),
  };
};

const checkShouldTableHide = (
  orderbookData: Partial<
    Pick<CompanyOrderbook, 'bid' | 'offer' | 'symbol'>
  > = {},
) => {
  const { symbol } = orderbookData;
  const { bidPrice, offerPrice } = getBidOfferPrice(orderbookData);

  const isIHSG = symbol === 'IHSG';
  const isBidOfferNotExist = bidPrice.length === 0 && offerPrice.length === 0;
  const isHideTable = isIHSG || isBidOfferNotExist;

  return { isHideTable, isIHSG };
};

const formatLot = (value) => {
  const result = parseCountAbbrv2(value, 1);
  if (typeof result === 'number') {
    return result.toString();
  }
  return result;
};

const isTensOfMillions = (value: string) => value.length > 9;

const generateColumnGroup = () => (
  <colgroup>
    <col style={{ width: '40px' }} />
    <col style={{ width: '66.5px' }} />
    <col style={{ width: '50px' }} />
    <col style={{ width: '50px' }} />
    <col style={{ width: '66.5px' }} />
    <col style={{ width: '40px' }} />
  </colgroup>
);

const getShowIepIev = (
  showIepIev: boolean,
  timeLeft: number | undefined,
): boolean => showIepIev && (timeLeft !== undefined ? timeLeft > 0 : true);

export {
  generateOHLCData,
  generateOrderbookData,
  formatNumber,
  getTypeByComparison,
  getType,
  handleReceiveMessageNATSLivePrice,
  handleReceiveMessageNATSOrderbook,
  handleReceiveMessageNATSIepIev,
  formatChangePrevious,
  formatNumberWithNA,
  formatPercentageChange,
  formatAverage,
  formatLastPrice,
  getPriceDataFromOrderbookData,
  calculateSummary,
  checkShouldTableHide,
  formatLot,
  isTensOfMillions,
  generateColumnGroup,
  getShowIepIev,
};
